import React, { useCallback, useEffect, useRef, useState } from "react"
import Autosuggest, { AutosuggestProps } from "react-autosuggest"
import styled from "styled-components"

import { HasDisplay } from "~/@shared/api-types/id-name-display"
import Input from "~/components/atoms/Input"
import { Color, RadiusSize, Shadow, Transition } from "~/consts/style-mixins"

type InfraAutosuggestProps = AutosuggestProps<unknown, unknown>

export type InputAutoSuggestProps<Suggest> = {
  /** 候補が`HasDisplay`ではないとき(例: skills) getterを指定する */
  getter?: (suggest: Suggest) => string
  id?: string
  placeholder?: string
  onChange?: (value: string) => void

  /** @deprecated 古いプロフィールのコードで使用している TODO(old-profile-page): リファクタしたら消す */
  inputState?: { value: string; setter: (_: string) => void }
  /** テキストボックスの中身と同期される値(基本的にdefaultValueを使用する) */
  value?: string
  /** 初期値(valueと違い、テキストボックスの中身と同期されない) */
  defaultValue?: string
  suggestions: Suggest[]
  onSelectSuggestion?: (value: string) => void
  error?: boolean
}

const defaultGetter = (suggest: HasDisplay) => {
  if (typeof suggest.display !== "string") {
    throw new Error(`<InputAutoSuggest />のgetterが正しく指定されていません`)
  }
  return suggest.display
}

/** @see https://github.com/moroshko/react-autosuggest#basic-usage */
const filterSuggestions = <S,>(getter: (_: S) => string, value: string, list: S[]): S[] => {
  const inputValue = value.toLowerCase().trim()
  const inputLength = inputValue.length

  if (inputLength === 0) {
    return []
  }

  // 例えばinputValueが"c"か"C"の時、"c"か"C"から始まる項目のみをfilterする
  return list.filter((suggest) => getter(suggest).toLowerCase().startsWith(inputValue))
}

export function InputAutoSuggest<Suggest = HasDisplay>(props: InputAutoSuggestProps<Suggest>) {
  const { id, value, defaultValue, suggestions, placeholder, onSelectSuggestion, onChange, error } = props
  const getter = (props.getter ?? defaultGetter) as (suggest: Suggest) => string

  const [inputValue, setInputValue] = useState(() => value ?? defaultValue ?? props.inputState?.value ?? "")
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggest[]>([])
  const previousValueRef = useRef(value)

  // const onChangeInput: InfraAutosuggestProps["inputProps"]["onChange"] = useCallback(
  // NOTE: 下のuseEffectでもonChangeInputを使用するため、無駄な型のある↑ではなく最低限の型の↓でやってる
  const onChangeInput = useCallback(
    (event: unknown, params: { newValue: string }) => {
      const { newValue } = params
      setInputValue(newValue)
      props.inputState?.setter(newValue)
      onChange?.(newValue)
    },
    [onChange]
  )

  useEffect(() => {
    if (value == null || value === previousValueRef.current) {
      return
    }
    previousValueRef.current = value
    onChangeInput(undefined, { newValue: value })
  }, [value])

  const onSuggestionsFetchRequested: InfraAutosuggestProps["onSuggestionsFetchRequested"] = useCallback(
    ({ value }) => {
      setFilteredSuggestions(filterSuggestions(getter, value, suggestions))
    },
    [getter, suggestions]
  )

  const onSuggestionsClearRequested: InfraAutosuggestProps["onSuggestionsClearRequested"] = () => {
    setFilteredSuggestions([])
  }

  const onSuggestionSelected: InfraAutosuggestProps["onSuggestionSelected"] = (event, { suggestionValue }) => {
    event.preventDefault()
    onSelectSuggestion?.(suggestionValue)
  }

  return (
    <Autosuggest
      suggestions={filteredSuggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={onSuggestionSelected}
      getSuggestionValue={getter}
      inputProps={{ id, placeholder, value: inputValue, onChange: onChangeInput }}
      renderInputComponent={(props) => <Input {...props} size="Medium" isError={error} />}
      renderSuggestion={(suggest, { isHighlighted }) => (
        <StyledSuggestion isHighlighted={isHighlighted}>{getter(suggest)}</StyledSuggestion>
      )}
      renderSuggestionsContainer={({ containerProps, children }) =>
        children && (
          <StyledSuggestionsContainerWrap {...containerProps}>
            <StyledSuggestionsContainer>{children}</StyledSuggestionsContainer>
          </StyledSuggestionsContainerWrap>
        )
      }
    />
  )
}

const StyledSuggestionsContainerWrap = styled.div`
  position: relative;
  user-select: none;
`

const StyledSuggestionsContainer = styled.div`
  position: absolute;
  width: 100%;
  background-color: ${Color.White};
  border-radius: ${RadiusSize.Small}px;
  z-index: 2;
  overflow: hidden;
  ${Shadow.Shallow};
  margin-top: 8px;
  padding: 8px;
`

const StyledSuggestion = styled.div<{ isHighlighted?: boolean }>`
  cursor: pointer;
  padding: 8px;
  border-radius: ${RadiusSize.Small}px;
  ${Transition.Hover};

  background-color: ${({ isHighlighted }) => isHighlighted && Color.BorderGray};
`
